import { useState, useEffect, useMemo } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { useSocket } from '@/lib/socket';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableCaption,
} from '@/components/ui/table';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { useLocationContext } from '@/contexts/LocationContext';
import { Button } from '@/components/ui/button';
import { APP_VERSION, COMMIT_HASH } from '@/lib/version';
import { useAuth } from '@/contexts/AuthContext';
import { UserRole } from '@/lib/types';
import { useLoading } from '@/contexts/LoadingContext';
import { Loader2 } from 'lucide-react';

// New interfaces to match the RESTlet structure
interface WorkOrder {
  id: string;
  orderID: string;
  index: string;
  isReady: boolean;
  fabricType: string;
  frameColorVaries: boolean;
  frameColor: string;
  buildoutFrameColor: string;
  hemBarFrameColor: string;
  housingFrameColor: string;
  retentionFrameColor: string;
  schedulePriority: string;
  scheduleFrame: string;
  scheduleFabric: string;
  kittingComplete: boolean;
  metalComplete: boolean;
  fabricComplete: boolean;
  finalAssemblyComplete: boolean;
  woReady: boolean;
  isTKS0026?: boolean;
}

interface OtherItem {
  displayName: string;
  quantity: string;
  details: string;
  index: string;
  isReady: boolean;
}

interface SalesOrder {
  id: string;
  orderNumber: string;
  date: string;
  customer: string;
  location: string;
  workOrders: WorkOrder[];
  otherItems: OtherItem[];
}

// Combined item for display purposes
export interface DisplayItem {
  id: string;
  salesOrderId: string;
  orderID: string;
  lineIndex: string;
  customer: string;
  date: string;
  location: string;
  displayName?: string;
  quantity?: string;
  details?: string;
  isWorkOrder: boolean;
  woId?: string;
  // WO specific fields
  schedulePriority?: string;
  scheduleFrame?: string;
  scheduleFabric?: string;
  fabricType?: string;
  frameColorVaries?: boolean;
  frameColor?: string;
  buildoutFrameColor?: string;
  hemBarFrameColor?: string;
  housingFrameColor?: string;
  retentionFrameColor?: string;
  // Status fields
  kittingComplete: boolean;
  fabricComplete?: boolean;
  metalComplete?: boolean;
  finalAssemblyComplete?: boolean;
  // TKS-0026 field
  isTKS0026?: boolean;
  // For grouped display of other items
  otherItems?: OtherItem[];
  // For visual grouping
  otherItemGroup?: string;
  // Index signature for dynamic property access
  [key: string]: any;
}

const columnMappings: { [key: string]: string } = {
  orderID: 'ID',
  customer: 'Customer',
  date: 'Date',
  fabricDetails: 'Fabric Details',
  frameColorDetails: 'Powder Details',
  itemDetails: 'Item Details',
  kittingComplete: 'Kitting',
  fabricComplete: 'Fabric',
  metalComplete: 'Metal',
  finalAssemblyComplete: 'Final Assembly',
  actions: 'Actions',
  status: 'Status',
};

const sortColumns = ['date', 'schedulePriority', 'scheduleFrame', 'scheduleFabric'];

interface NetSuiteTableProps {
  filterFn: (data: DisplayItem[]) => DisplayItem[];
  onRowClick?: (row: DisplayItem) => void;
  currentFilter: 'all' | 'kitting' | 'fabric' | 'metal' | 'assembly';
}

export function NetSuiteTable({
  filterFn,
  onRowClick,
  currentFilter
}: NetSuiteTableProps) {
  const socket = useSocket();
  const [data, setData] = useState<DisplayItem[]>([]);
  const [rawData, setRawData] = useState<DisplayItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const [selectedRow, setSelectedRow] = useState<DisplayItem | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { selectedLocation } = useLocationContext();
  const { currentUser } = useAuth();
  const { addLoadingState, removeLoadingState, removeLoadingStateByItemId, isItemLoading } = useLoading();

  // Check if user is a manager or admin
  const isManagerOrAdmin = useMemo(() => {
    if (!currentUser) return false;
    return currentUser.roles.includes(UserRole.MANAGER) || 
           currentUser.roles.includes(UserRole.ADMIN);
  }, [currentUser]);

  // Request initial data on mount
  useEffect(() => {
    if (socket) {
      socket.emit('requestNetSuiteData');
      
      // Add timeout for loading state
      const loadingTimeout = setTimeout(() => {
        if (isLoading) {
          setIsLoading(false);
          toast({
            title: 'Error',
            description: 'Failed to load data. Please try refreshing the page.',
            variant: 'destructive',
          });
        }
      }, 10000); // 10 second timeout

      return () => clearTimeout(loadingTimeout);
    }
  }, [socket, isLoading]);

  // Handle raw data updates from socket
  useEffect(() => {
    if (socket) {
      let updateTimeout: NodeJS.Timeout;
      
      const debouncedHandleNetSuiteData = (newData: SalesOrder[]) => {
        clearTimeout(updateTimeout);
        updateTimeout = setTimeout(() => {
                    // Use requestAnimationFrame to defer heavy processing
          requestAnimationFrame(() => {
          // Process sales orders to create display items
          const displayItems: DisplayItem[] = [];
        
        if (Array.isArray(newData)) {
          newData.forEach(so => {
            // Process work orders
            if (Array.isArray(so.workOrders)) {
              so.workOrders.forEach(wo => {
                displayItems.push({
                  id: wo.id,
                  salesOrderId: so.id,
                  orderID: wo.orderID,
                  lineIndex: wo.index,
                  customer: so.customer,
                  date: so.date,
                  location: so.location || 'No Location',
                  isWorkOrder: true,
                  woId: wo.id,
                  schedulePriority: wo.schedulePriority,
                  scheduleFrame: wo.scheduleFrame,
                  scheduleFabric: wo.scheduleFabric,
                  fabricType: wo.fabricType,
                  frameColorVaries: wo.frameColorVaries,
                  frameColor: wo.frameColor,
                  buildoutFrameColor: wo.buildoutFrameColor,
                  hemBarFrameColor: wo.hemBarFrameColor,
                  housingFrameColor: wo.housingFrameColor,
                  retentionFrameColor: wo.retentionFrameColor,
                  kittingComplete: wo.kittingComplete,
                  fabricComplete: wo.fabricComplete,
                  metalComplete: wo.metalComplete,
                  finalAssemblyComplete: wo.finalAssemblyComplete,
                  isTKS0026: wo.isTKS0026
                });
              });
            }
            
            // Process other items - group them by same displayName
            if (Array.isArray(so.otherItems) && so.otherItems.length > 0) {
              // Create a display item for each OTHER ITEM (not grouped) to allow individual completion
              so.otherItems.forEach(item => {
                displayItems.push({
                  id: `${so.id}-${item.index}`,
                  salesOrderId: so.id,
                  orderID: so.orderNumber,
                  lineIndex: item.index,
                  customer: so.customer,
                  date: so.date,
                  location: so.location || 'No Location',
                  isWorkOrder: false,
                  displayName: item.displayName,
                  quantity: item.quantity,
                  details: item.details,
                  kittingComplete: item.isReady,
                  fabricComplete: item.isReady,
                  metalComplete: item.isReady,
                  finalAssemblyComplete: item.isReady,
                  // Store the group info for visual grouping
                  otherItemGroup: item.displayName,
                  isTKS0026: item.isReady
                });
              });
            }
          });
        }
        
        // Sort the data
        const sortedData = [...displayItems].sort((a, b) => {
          // First sort by priority - convert to number or default to 0
          const priorityA = a.schedulePriority ? parseInt(a.schedulePriority) || 0 : 0;
          const priorityB = b.schedulePriority ? parseInt(b.schedulePriority) || 0 : 0;
          
          if (priorityA > priorityB) return -1;
          if (priorityA < priorityB) return 1;

          // Then sort by date
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          if (dateA < dateB) return -1;
          if (dateA > dateB) return 1;

          // Then sort by other columns
          for (const column of sortColumns) {
            if (column === 'schedulePriority') continue;
            if ((a[column] || '') < (b[column] || '')) return -1;
            if ((a[column] || '') > (b[column] || '')) return 1;
          }
          return 0;
        });
        
        setRawData(sortedData);
        setIsLoading(false);
      });
    }, 100);
  };
  
  socket.on('netsuiteData', debouncedHandleNetSuiteData);

      // Handle loading state broadcasts
      socket.on('itemLoading', (data: {
        itemId: string;
        message: string;
        userId: string;
        action: string;
        userEmail: string;
        currentFilter?: string;
      }) => {
        console.log('🔄 Received loading state broadcast:', data);
        
        // Only show loading state if we're on the same page/filter
        if (!data.currentFilter || data.currentFilter === currentFilter) {
          // Don't add duplicate loading states for the same item
          if (!isItemLoading(data.itemId)) {
            addLoadingState({
              itemId: data.itemId,
              message: data.message,
              userId: data.userId,
              userEmail: data.userEmail
            });
          }
        }
      });

      socket.on('itemLoadingComplete', (data: {
        itemId: string;
        userId: string;
        action: string;
      }) => {
        console.log('🔄 Received loading complete broadcast:', data);
        // Remove loading state for this item
        removeLoadingStateByItemId(data.itemId);
      });

      socket.on('netsuiteError', (error) => {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        });
        setIsLoading(false);
      });

      socket.on('connect', () => {
        toast({
          title: 'Connected',
          description: 'Connected to server successfully',
        });
      });

      socket.on('disconnect', () => {
        toast({
          title: 'Disconnected',
          description: 'Lost connection to server',
          variant: 'destructive',
        });
      });

      return () => {
        socket.off('netsuiteData', debouncedHandleNetSuiteData);
        socket.off('netsuiteError');
        socket.off('connect');
        socket.off('disconnect');
        socket.off('itemLoading');
        socket.off('itemLoadingComplete');
        clearTimeout(updateTimeout);
      };
    }
  }, [socket, toast, currentUser, addLoadingState, removeLoadingState, removeLoadingStateByItemId, currentFilter]);

  // Apply filter whenever raw data or filter function changes
  useEffect(() => {
    setData(filterFn ? filterFn(rawData) : rawData);
  }, [rawData, filterFn]);

  const handleRowClick = (row: DisplayItem) => {
    if (onRowClick) {
      setSelectedRow(row);
      setIsDialogOpen(true);
    }
  };

  const handleConfirm = async () => {
    if (selectedRow && onRowClick) {
      // Close modal immediately
      setIsDialogOpen(false);
      setSelectedRow(null);
      
      // Determine the stage being updated based on current filter
      const getStageMessage = () => {
        switch (currentFilter) {
          case 'kitting':
            return 'Kitting';
          case 'fabric':
            return 'Fabric';
          case 'metal':
            return 'Metal';
          case 'assembly':
            return 'Final Assembly';
          default:
            return 'Status';
        }
      };

      const stageMessage = getStageMessage();
      const itemType = selectedRow.isWorkOrder 
        ? (selectedRow.isTKS0026 ? 'Buildout' : 'Screen Unit')
        : 'Item';
      const orderId = selectedRow.isWorkOrder ? selectedRow.orderID : selectedRow.orderID;

      const loadingMessage = `Updating ${itemType} #${orderId} - ${stageMessage}`;

      // Broadcast loading state to backend (don't add locally yet)
      if (socket && currentUser) {
        console.log('🔄 Broadcasting loading state:', {
          itemId: selectedRow.id,
          message: loadingMessage,
          userId: currentUser._id,
          action: 'status_update',
          currentFilter: currentFilter
        });
        socket.emit('startLoading', {
          itemId: selectedRow.id,
          message: loadingMessage,
          userId: currentUser._id,
          action: 'status_update',
          currentFilter: currentFilter
        });
      }

      try {
        await onRowClick(selectedRow);
      } catch (error) {
        // Error handling is done in FilteredNetSuiteTable
      } finally {
        // Broadcast loading completion
        if (socket && currentUser) {
          socket.emit('stopLoading', {
            itemId: selectedRow.id,
            userId: currentUser._id,
            action: 'status_update'
          });
        }
      }
    }
  };

  // Filter data based on selected location and user permissions
  const filteredData = useMemo(() => {
    if (!selectedLocation) return data;
    
    // If "All, USA" is selected, check if user has permission to see all locations
    if (selectedLocation === 'All, USA') {
      if (currentUser) {
        const isAdmin = currentUser.roles.includes(UserRole.ADMIN);
        const hasAllLocationsAccess = currentUser.roles.includes(UserRole.ALL_LOCATIONS);
        
        if (isAdmin || hasAllLocationsAccess) {
          return data;
        }
      }
      
      // If user doesn't have all locations access, don't show any data
      return [];
    }
    
    // Otherwise, filter by the selected location
    return data.filter((item) => {
      const lastSpaceIndex = item.location.lastIndexOf(' ');
      const city = item.location.slice(0, lastSpaceIndex).trim();
      const state = item.location.slice(lastSpaceIndex + 1).trim();
      return `${city}, ${state}` === selectedLocation;
    });
  }, [data, selectedLocation, currentUser]);

  const getRelevantColumns = () => {
    const baseColumns = ['orderID'];
    
    switch (currentFilter) {
      case 'all':
        return [...baseColumns, 'fabricDetails', 'frameColorDetails', 'itemDetails', 'status'];
      case 'kitting':
        return [...baseColumns, 'itemDetails', 'actions'];
      case 'fabric':
        return [...baseColumns, 'fabricDetails', 'actions'];
      case 'metal':
        return [...baseColumns, 'frameColorDetails', 'actions'];
      case 'assembly':
        return [...baseColumns, 'actions'];
      default:
        return baseColumns;
    }
  };

  const displayColumns = getRelevantColumns().filter((column) => {
    // On mobile, only show essential columns
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return ['orderID', 'customer', 'actions'].includes(column);
    }
    return true;
  });

  const renderCell = (row: DisplayItem, column: string) => {
    // Handle the special case of orderID column - add external link button for work orders
    if (column === 'orderID') {
      if (row.isWorkOrder && (currentFilter === 'all' || isManagerOrAdmin)) {
        return (
          <div 
            onClick={() => window.open(`https://9259008.app.netsuite.com/app/accounting/transactions/workord.nl?id=${row.woId}`, '_blank')}
            className="flex items-center gap-1 cursor-pointer hover:text-blue-600 transition-colors"
            title="View in NetSuite"
          >
            <span>{row.orderID}</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 12l4-4m0 0l-4-4m4 4H2"/><path d="M5 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-7"/></svg>
          </div>
        );
      }
      return row.orderID;
    }
    
    // Handle the special case of itemDetails which is different for WO vs non-WO items
    if (column === 'itemDetails') {
      if (!row.isWorkOrder && row.displayName) {
        return `${row.displayName} (${row.quantity}) - ${row.details || 'No details'}`;
      }
      // For work orders, display as Screen Unit or Buildout based on TKS-0026 flag
      if (row.isTKS0026) {
        return 'Buildout';
      }
      return 'Screen Unit';
    }
    
    // Handle fabricDetails and frameColorDetails
    if (column === 'fabricDetails') {
      return row.fabricType || '-';
    }
    
    if (column === 'frameColorDetails') {
      if (row.frameColorVaries) {
        return (
          <div className="text-sm space-y-1">
            <div><b>Housing:</b> {row.housingFrameColor}</div>
            <div><b>Buildout:</b> {row.buildoutFrameColor}</div>
            <div><b>Hem Bar:</b> {row.hemBarFrameColor}</div>
            <div><b>Retention:</b> {row.retentionFrameColor}</div>
          </div>
        );
      }
      return row.frameColor || '-';
    }

    // Status column with stage dots - only shown on "all" tab
    if (column === 'status') {
      // For non-work order items, show only one status indicator
      if (!row.isWorkOrder) {
        return (
          <div className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${row.kittingComplete ? 'bg-green-500' : 'bg-gray-300'}`} title="Status"></div>
          </div>
        );
      }
      
      // For TKS-0026 items, only show metal and final assembly phases
      if (row.isTKS0026) {
        return (
          <div className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${row.metalComplete ? 'bg-green-500' : 'bg-gray-300'}`} title="Metal"></div>
            <div className={`w-2 h-2 rounded-full ${row.finalAssemblyComplete ? 'bg-green-500' : 'bg-gray-300'}`} title="Assembly"></div>
          </div>
        );
      }
      
      // For regular work orders, show all stages
      return (
        <div className="flex items-center gap-1">
          <div className={`w-2 h-2 rounded-full ${row.kittingComplete ? 'bg-green-500' : 'bg-gray-300'}`} title="Kitting"></div>
          <div className={`w-2 h-2 rounded-full ${row.fabricComplete ? 'bg-green-500' : 'bg-gray-300'}`} title="Fabric"></div>
          <div className={`w-2 h-2 rounded-full ${row.metalComplete ? 'bg-green-500' : 'bg-gray-300'}`} title="Metal"></div>
          <div className={`w-2 h-2 rounded-full ${row.finalAssemblyComplete ? 'bg-green-500' : 'bg-gray-300'}`} title="Assembly"></div>
        </div>
      );
    }
    
    const value = row[column];
    
    if (column === 'date') {
      return new Date(value).toLocaleDateString();
    }
    
    if (typeof value === 'string' && value.includes('<br />')) {
      // Split by <br /> and return as proper React components
      return (
        <div className="space-y-1">
          {value.split('<br />').map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      );
    }
    
    return value;
  };

  if (isLoading) {
    return <div className='text-center text-lg'>Loading...</div>;
  }

  if (!data.length) {
    return <div className='text-center text-lg'>All items are up to date!</div>;
  }

  return (
    <div className="space-y-6">
      {/* Desktop Table */}
      <div className='hidden md:block'>
                        <div className="bg-transparent rounded-2xl shadow-elegant border border-white/50 overflow-hidden hover:shadow-dreamy transition-all duration-300">
          <Table>
            <TableCaption className="text-slate-500 font-medium py-4">Version {APP_VERSION} ({COMMIT_HASH})</TableCaption>
          <TableHeader>
            <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
              {displayColumns.map((column) => (
                <TableHead key={column} className="whitespace-nowrap font-bold text-slate-700 py-4 px-6 tracking-wide">
                  {columnMappings[column]}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* Group by sales order for visual grouping */}
            {(() => {
              // Group by sales order ID first
              const salesOrderGroups: { [key: string]: DisplayItem[] } = {};
              
              filteredData.forEach(item => {
                if (!salesOrderGroups[item.salesOrderId]) {
                  salesOrderGroups[item.salesOrderId] = [];
                }
                salesOrderGroups[item.salesOrderId].push(item);
              });
              
              // Create an array of JSX elements
              const tableRows: JSX.Element[] = [];
              
              Object.entries(salesOrderGroups).forEach(([salesOrderId, items], orderIndex) => {
                // Use alternating background colors for adjacent orders
                const isEvenOrder = orderIndex % 2 === 0;
                const headerBgColor = isEvenOrder ? "bg-blue-50" : "bg-purple-50";
                const headerBorderColor = isEvenOrder ? "border-t-blue-200" : "border-t-purple-200";
                const headerTextColor = isEvenOrder ? "text-blue-800" : "text-purple-800";
                
                // Sort items to keep same otherItemGroups together
                items.sort((a, b) => {
                  // First, group work orders together
                  if (a.isWorkOrder && !b.isWorkOrder) return -1;
                  if (!a.isWorkOrder && b.isWorkOrder) return 1;
                  
                  // Then sort other items by their group
                  if (!a.isWorkOrder && !b.isWorkOrder) {
                    if (a.otherItemGroup && b.otherItemGroup) {
                      if (a.otherItemGroup < b.otherItemGroup) return -1;
                      if (a.otherItemGroup > b.otherItemGroup) return 1;
                    }
                  }
                  
                  return 0;
                });
                
                // Add a transparent spacer row before each order (except the first one)
                if (orderIndex > 0) {
                  tableRows.push(
                    <TableRow key={`spacer-${salesOrderId}`} className="bg-transparent">
                      <TableCell colSpan={displayColumns.length} className="h-8 bg-transparent border-none p-0"></TableCell>
                    </TableRow>
                  );
                }
                
                // Add an order header with customer info
                const firstItem = items[0];
                
                // Calculate the progress percentage with proper weighting
                let totalWeight = 0;
                let completedWeight = 0;
                
                items.forEach(item => {
                  // For TKS-0026 work orders, only metal and final assembly steps are required
                  if (item.isWorkOrder && item.isTKS0026) {
                    totalWeight += 2; // Only 2 steps for TKS-0026
                    if (item.metalComplete) completedWeight += 1;
                    if (item.finalAssemblyComplete) completedWeight += 1;
                  }
                  // For standard work orders, each has 4 steps with equal weight
                  else if (item.isWorkOrder) {
                    totalWeight += 4;
                    if (item.kittingComplete) completedWeight += 1;
                    if (item.fabricComplete) completedWeight += 1;
                    if (item.metalComplete) completedWeight += 1;
                    if (item.finalAssemblyComplete) completedWeight += 1;
                  } else {
                    // For non-work orders, only one status with same weight as WO
                    totalWeight += 1;
                    if (item.kittingComplete) completedWeight += 1;
                  }
                });
                
                const progressPercent = totalWeight > 0 ? Math.round((completedWeight / totalWeight) * 100) : 0;
                
                tableRows.push(
                  <TableRow key={`header-${salesOrderId}`} className={`${headerBgColor} border-t-4 ${headerBorderColor} shadow-lg hover:shadow-lg transition-shadow duration-100`}>
                    <TableCell colSpan={displayColumns.length} className="py-4 px-6">
                      <div className="flex flex-col space-y-2">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="flex items-center">
                              <div className={currentFilter === 'all' || isManagerOrAdmin
                                ? `font-bold text-lg ${headerTextColor} flex items-center gap-1 cursor-pointer hover:text-blue-600 transition-colors duration-100`
                                : `font-bold text-lg ${headerTextColor}`}
                                   onClick={currentFilter === 'all' || isManagerOrAdmin ? () => window.open(`https://9259008.app.netsuite.com/app/accounting/transactions/salesord.nl?id=${salesOrderId}`, '_blank') : undefined}
                                   title={currentFilter === 'all' || isManagerOrAdmin ? "View in NetSuite" : undefined}
                              >
                                Order #{firstItem.orderID.split('-')[0]}
                                {(currentFilter === 'all' || isManagerOrAdmin) && (
                                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 12l4-4m0 0l-4-4m4 4H2"/><path d="M5 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-7"/></svg>
                                )}
                              </div>
                            </div>
                            <div className="font-medium">
                              {firstItem.customer}
                            </div>
                            <div className="text-xs text-gray-600">
                              {new Date(firstItem.date).toLocaleDateString()} - {items.length} {items.length === 1 ? 'item' : 'items'}
                            </div>
                          </div>
                          {currentFilter === 'all' && (
                            <div className="flex items-center gap-1 text-xs">
                              <span>{progressPercent}%</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Progress bar - only shown on "all" tab */}
                        {currentFilter === 'all' && (
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div 
                              className="bg-green-500 h-1.5 rounded-full" 
                              style={{ width: `${progressPercent}%` }}
                            ></div>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
                
                // Track the current other item group to add subheaders
                let currentOtherItemGroup = '';
                
                items.forEach((row, rowIndex) => {
                  // Add a subheader for other item groups
                  if (!row.isWorkOrder && row.otherItemGroup && row.otherItemGroup !== currentOtherItemGroup) {
                    currentOtherItemGroup = row.otherItemGroup;
                    tableRows.push(
                      <TableRow key={`subheader-${salesOrderId}-${row.otherItemGroup}`} className="bg-gray-50 border-x-2 border-x-gray-300">
                        <TableCell colSpan={displayColumns.length} className="py-1">
                          <div className="text-sm font-medium">{row.otherItemGroup}</div>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  
                  // Special styling for grouped rows
                  const rowClass = `
                    hover:bg-gray-50/60 
                    transition-colors duration-100
                    cursor-default
                    ${rowIndex % 2 === 0 ? 'bg-white/90' : 'bg-gray-50/60'}
                  `;
                  
                  tableRows.push(
              <TableRow 
                key={row.id} 
                      className={`${rowClass} border-l-2 ${isEvenOrder ? 'border-l-blue-100' : 'border-l-purple-100'}`}
              >
                {displayColumns.map((column) => (
                  <TableCell key={column} className={
                    column === 'orderID' || column === 'actions' 
                      ? 'whitespace-nowrap' 
                      : 'whitespace-normal break-words max-w-md'
                  }>
                    {column === 'actions' && onRowClick ? (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(row);
                        }}
                        size='sm'
                        variant='outline'
                        disabled={isItemLoading(row.id)}
                        className="flex items-center gap-2"
                      >
                        {isItemLoading(row.id) ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span className="hidden sm:inline">Updating...</span>
                          </>
                        ) : (
                          'Mark Complete'
                        )}
                      </Button>
                    ) : (
                            renderCell(row, column)
                    )}
                  </TableCell>
                ))}
              </TableRow>
                  );
                });
              });
              
              return tableRows;
            })()}
          </TableBody>
          </Table>
        </div>
      </div>

      {/* Mobile Card Layout */}
      <div className='grid grid-cols-1 gap-6 md:hidden'>
        {/* Group by sales order for visual grouping */}
        {(() => {
          // Group by sales order ID first
          const salesOrderGroups: { [key: string]: DisplayItem[] } = {};
          
          filteredData.forEach(item => {
            if (!salesOrderGroups[item.salesOrderId]) {
              salesOrderGroups[item.salesOrderId] = [];
            }
            salesOrderGroups[item.salesOrderId].push(item);
          });
          
          // Create an array of JSX elements for mobile cards
          const mobileCards: JSX.Element[] = [];
          
          Object.entries(salesOrderGroups).forEach(([salesOrderId, items], orderIndex) => {
            // Use alternating background colors for adjacent orders
            const isEvenOrder = orderIndex % 2 === 0;
            const headerBgColor = isEvenOrder ? "bg-blue-50" : "bg-purple-50";
            const headerBorderColor = isEvenOrder ? "border-blue-200" : "border-purple-200";
            const headerTextColor = isEvenOrder ? "text-blue-800" : "text-purple-800";
            
            // Add a separator between orders (except the first one)
            if (orderIndex > 0) {
              mobileCards.push(
                <div key={`separator-${salesOrderId}`} className="border-b-2 border-gray-300 my-8"></div>
              );
            }
            
            // Sort items to keep same otherItemGroups together
            items.sort((a, b) => {
              // First, group work orders together
              if (a.isWorkOrder && !b.isWorkOrder) return -1;
              if (!a.isWorkOrder && b.isWorkOrder) return 1;
              
              // Then sort other items by their group
              if (!a.isWorkOrder && !b.isWorkOrder) {
                if (a.otherItemGroup && b.otherItemGroup) {
                  if (a.otherItemGroup < b.otherItemGroup) return -1;
                  if (a.otherItemGroup > b.otherItemGroup) return 1;
                }
              }
              
              return 0;
            });
            
            // Add an order header with customer info
            const firstItem = items[0];
            
            // Calculate the progress percentage with proper weighting
            let totalWeight = 0;
            let completedWeight = 0;
            
            items.forEach(item => {
              // For TKS-0026 work orders, only metal and final assembly steps are required
              if (item.isWorkOrder && item.isTKS0026) {
                totalWeight += 2; // Only 2 steps for TKS-0026
                if (item.metalComplete) completedWeight += 1;
                if (item.finalAssemblyComplete) completedWeight += 1;
              }
              // For standard work orders, each has 4 steps with equal weight
              else if (item.isWorkOrder) {
                totalWeight += 4;
                if (item.kittingComplete) completedWeight += 1;
                if (item.fabricComplete) completedWeight += 1;
                if (item.metalComplete) completedWeight += 1;
                if (item.finalAssemblyComplete) completedWeight += 1;
              } else {
                // For non-work orders, only one status with same weight as WO
                totalWeight += 1;
                if (item.kittingComplete) completedWeight += 1;
              }
            });
            
            const progressPercent = totalWeight > 0 ? Math.round((completedWeight / totalWeight) * 100) : 0;
            
            mobileCards.push(
              <div key={`header-${salesOrderId}`} className={`${headerBgColor} rounded-t-xl p-5 shadow-elegant border-2 ${headerBorderColor} mt-8`}>
                <div className="flex items-center">
                  <div className={currentFilter === 'all' || isManagerOrAdmin
                    ? `font-bold text-lg ${headerTextColor} flex items-center gap-1 cursor-pointer hover:text-blue-600 transition-colors`
                    : `font-bold text-lg ${headerTextColor}`}
                       onClick={currentFilter === 'all' || isManagerOrAdmin ? () => window.open(`https://9259008.app.netsuite.com/app/accounting/transactions/salesord.nl?id=${salesOrderId}`, '_blank') : undefined}
                       title={currentFilter === 'all' || isManagerOrAdmin ? "View in NetSuite" : undefined}
                  >
                    Order #{firstItem.orderID.split('-')[0]}
                    {(currentFilter === 'all' || isManagerOrAdmin) && (
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 12l4-4m0 0l-4-4m4 4H2"/><path d="M5 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-7"/></svg>
                    )}
                  </div>
                </div>
                <div className="font-medium">{firstItem.customer}</div>
                <div className="text-xs text-gray-600">
                  {new Date(firstItem.date).toLocaleDateString()} - {items.length} {items.length === 1 ? 'item' : 'items'}
                </div>
                
                {/* Progress bar - only shown on "all" tab */}
                {currentFilter === 'all' && (
                  <div className="flex items-center gap-2 mt-2 mb-1">
                    <div className="flex-grow bg-gray-200 rounded-full h-1.5">
                      <div 
                        className="bg-green-500 h-1.5 rounded-full" 
                        style={{ width: `${progressPercent}%` }}
                      ></div>
                    </div>
                    <span className="text-xs font-medium">{progressPercent}%</span>
                  </div>
                )}
              </div>
            );
            
            // Track the current other item group to add subheaders
            let currentOtherItemGroup = '';
            
            // Add each item in the group
            items.forEach((row, rowIndex) => {
              // Add a subheader for other item groups
              if (!row.isWorkOrder && row.otherItemGroup && row.otherItemGroup !== currentOtherItemGroup) {
                currentOtherItemGroup = row.otherItemGroup;
                mobileCards.push(
                  <div key={`subheader-${salesOrderId}-${row.otherItemGroup}`} className="bg-gray-50 p-2 text-sm font-medium border-x-2 border-x-gray-300">
                    {row.otherItemGroup}
                  </div>
                );
              }
              
              const isLastInGroup = rowIndex === items.length - 1;
              const isLastOfType = items.findIndex((item, idx) => 
                idx > rowIndex && 
                ((row.isWorkOrder && !item.isWorkOrder) || 
                 (!row.isWorkOrder && (item.otherItemGroup !== row.otherItemGroup)))
              ) === -1;
              
              mobileCards.push(
                <div 
                  key={row.id} 
                  className={`bg-white/95 border border-gray-200 shadow-soft hover:shadow-lg p-5 transition-all duration-200 hover:scale-[1.02] hover:-translate-y-1
                     ${rowIndex > 0 && currentOtherItemGroup === row.otherItemGroup ? 'border-t-0' : ''}
                     ${isLastInGroup || isLastOfType ? 'rounded-b-xl mb-6' : 'rounded-none'}
                     border-l-4 ${isEvenOrder ? 'border-l-blue-300' : 'border-l-purple-300'}`}
                >
            <div className='space-y-3'>
              <div className='flex justify-between items-start'>
                <div>
                        <div className='font-semibold text-gray-900'>
                          {row.isWorkOrder ? (
                            currentFilter === 'all' || isManagerOrAdmin ? (
                              <div 
                                className="flex items-center gap-1 cursor-pointer hover:text-blue-600 transition-colors duration-100"
                                onClick={() => window.open(`https://9259008.app.netsuite.com/app/accounting/transactions/workord.nl?id=${row.woId}`, '_blank')}
                                title="View in NetSuite"
                              >
                                <span>{row.isTKS0026 ? 'Buildout: ' : 'Screen Unit: '}#{row.orderID}</span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 12l4-4m0 0l-4-4m4 4H2"/><path d="M5 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-7"/></svg>
                              </div>
                            ) : (
                              <span>{row.isTKS0026 ? 'Buildout: ' : 'Screen Unit: '}#{row.orderID}</span>
                            )
                          ) : (
                            <>Other Item: #{row.orderID}</>
                          )}
                        </div>
                </div>
                {onRowClick && (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick(row);
                    }}
                    size='sm'
                    variant='outline'
                    className="shrink-0 flex items-center gap-2"
                    disabled={isItemLoading(row.id)}
                  >
                    {isItemLoading(row.id) ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="hidden sm:inline">Updating...</span>
                      </>
                    ) : (
                      'Mark Complete'
                    )}
                  </Button>
                )}
              </div>

                    {/* Show item details for all filters */}
                    {!row.isWorkOrder && row.displayName && (
                      <div className='text-sm'>
                        <div className='font-medium mb-1'>Item Details:</div>
                        <div className='text-muted-foreground'>
                          {row.displayName} (Qty: {row.quantity}) - {row.details || 'No details'}
                        </div>
                      </div>
                    )}

              {/* Show details based on current filter */}
                    {(currentFilter === 'fabric' || currentFilter === 'all') && row.fabricType && (
                <div className='text-sm'>
                  <div className='font-medium mb-1'>Fabric Details:</div>
                        <div className='text-muted-foreground'>{row.fabricType}</div>
                </div>
              )}
                    
                    {(currentFilter === 'metal' || currentFilter === 'all') && 
                      (row.frameColor || row.frameColorVaries) && (
                <div className='text-sm'>
                  <div className='font-medium mb-1'>Powder Details:</div>
                  <div className='text-muted-foreground'>
                    {row.frameColorVaries ? (
                      <div className="space-y-1">
                        <div><b>Housing:</b> {row.housingFrameColor}</div>
                        <div><b>Buildout:</b> {row.buildoutFrameColor}</div>
                        <div><b>Hem Bar:</b> {row.hemBarFrameColor}</div>
                        <div><b>Retention:</b> {row.retentionFrameColor}</div>
                      </div>
                    ) : (
                      row.frameColor || '-'
                    )}
                  </div>
                </div>
              )}

                    {/* Progress indicators for individual row - only on all view */}
                    {currentFilter === 'all' && (
                      <div className='flex items-center gap-2 pt-2'>
                        {row.isWorkOrder ? (
                          row.isTKS0026 ? (
                            // For TKS-0026 items, only show metal and final assembly indicators
                            <>
                              <div className='flex items-center gap-1'>
                                <div className={`w-2 h-2 rounded-full ${row.metalComplete ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                <span className='text-xs'>M</span>
                              </div>
                              <div className='flex items-center gap-1'>
                                <div className={`w-2 h-2 rounded-full ${row.finalAssemblyComplete ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                <span className='text-xs'>A</span>
                              </div>
                            </>
                          ) : (
                            // For regular work orders, show all four indicators
                            <>
                              <div className='flex items-center gap-1'>
                                <div className={`w-2 h-2 rounded-full ${row.kittingComplete ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                <span className='text-xs'>K</span>
                              </div>
                              <div className='flex items-center gap-1'>
                                <div className={`w-2 h-2 rounded-full ${row.fabricComplete ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                <span className='text-xs'>F</span>
                              </div>
                              <div className='flex items-center gap-1'>
                                <div className={`w-2 h-2 rounded-full ${row.metalComplete ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                <span className='text-xs'>M</span>
                              </div>
                              <div className='flex items-center gap-1'>
                                <div className={`w-2 h-2 rounded-full ${row.finalAssemblyComplete ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                <span className='text-xs'>A</span>
                              </div>
                            </>
                          )
                        ) : (
                          <div className='flex items-center gap-1'>
                            <div className={`w-2 h-2 rounded-full ${row.kittingComplete ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                          </div>
                        )}
                      </div>
                    )}
            </div>
          </div>
              );
            });
          });
          
          return mobileCards;
        })()}
      </div>
      <ConfirmActionDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={handleConfirm}
        title='Confirm Status Update'
        description='Are you sure you want to update the status of this item?'
        isLoading={false}
      />
    </div>
  );
}
